import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Tag,
  message,
  Spin,
  Empty,
  Form,
  Input,
  Select,
  List,
  Typography,
  Popconfirm,
  Divider,
} from 'antd';
import {
  ArrowRightOutlined,
  CustomerServiceOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { supportTicketsApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const pickRoot = (res) => res?.data || res || {};

const ReplyHtml = ({ html }) => {
  const s = typeof html === 'string' ? html : '';
  const isHtml = s.includes('<');
  if (isHtml) {
    return (
      <div
        className="text-sm text-gray-800 dark:text-gray-200"
        style={{ fontFamily: 'CustomFont' }}
        dangerouslySetInnerHTML={{ __html: s }}
      />
    );
  }
  return (
    <div className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200" style={{ fontFamily: 'CustomFont' }}>
      {s || '—'}
    </div>
  );
};

const STATUS_META = {
  open: { text: 'باز', color: 'red' },
  pending: { text: 'در انتظار', color: 'gold' },
  resolved: { text: 'حل‌شده', color: 'green' },
  closed: { text: 'بسته‌شده', color: 'blue' },
};

const PRIORITY_META = {
  low: { text: 'پایین', color: 'default' },
  medium: { text: 'متوسط', color: 'blue' },
  high: { text: 'بالا', color: 'orange' },
  urgent: { text: 'فوری', color: 'red' },
};

const SupportTicketShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/settings/support-tickets', { state: buildGoBackState() });
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [replyForm] = Form.useForm();
  const [otherForm] = Form.useForm();
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [otherSubmitting, setOtherSubmitting] = useState(false);
  const [actionId, setActionId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supportTicketsApi.show(id);
      const root = pickRoot(res);
      setPayload(root);
      const t = root.ticket;
      if (t) {
        replyForm.setFieldsValue({
          status: t.status,
          message: '',
        });
        otherForm.setFieldsValue({
          agent_id: t.agent_id,
          type_id: t.support_ticket_type_id,
          priority: t.priority,
          status: t.status,
        });
      }
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت تیکت');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [id, replyForm, otherForm]);

  useEffect(() => {
    load();
  }, [load]);

  const ticket = payload?.ticket;
  const types = payload?.types || [];
  const agents = payload?.superadmins || [];
  const chart = payload?.['ticket-chart'] || payload?.ticket_chart;

  const onReply = async (values) => {
    setReplySubmitting(true);
    try {
      await supportTicketsApi.update(id, {
        status: values.status,
        message: values.message?.trim() || '',
      });
      message.success('ثبت شد');
      replyForm.setFieldsValue({ message: '' });
      load();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, replyForm);
      else message.error(error?.message || 'خطا');
    } finally {
      setReplySubmitting(false);
    }
  };

  const onOtherSave = async (values) => {
    setOtherSubmitting(true);
    try {
      await supportTicketsApi.updateOtherData(id, {
        agent_id: values.agent_id,
        type_id: values.type_id,
        priority: values.priority,
        status: values.status,
      });
      message.success('اطلاعات بروزرسانی شد');
      load();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, replyForm);
      else message.error(error?.message || 'خطا');
    } finally {
      setOtherSubmitting(false);
    }
  };

  const onDeleteReply = async (replyId) => {
    setActionId(`r-${replyId}`);
    try {
      await supportTicketsApi.deleteReply(replyId);
      message.success('پاسخ حذف شد');
      load();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف پاسخ');
    } finally {
      setActionId(null);
    }
  };

  const onDeleteFile = async (fileId) => {
    setActionId(`f-${fileId}`);
    try {
      await supportTicketsApi.deleteFile(fileId);
      message.success('فایل حذف شد');
      load();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف فایل');
    } finally {
      setActionId(null);
    }
  };

  const onDownloadFile = async (fileId) => {
    try {
      await supportTicketsApi.downloadFile(fileId);
    } catch (error) {
      message.error(error?.message || 'خطا در دانلود');
    }
  };

  const chartBars = useMemo(() => {
    if (!chart?.labels?.length) return null;
    const total = (chart.values || []).reduce((a, b) => a + Number(b || 0), 0) || 1;
    return (
      <div className="flex flex-wrap gap-2">
        {chart.labels.map((label, i) => {
          const v = Number(chart.values?.[i] || 0);
          const pct = Math.round((v / total) * 100);
          const color = chart.colors?.[i] || '#94a3b8';
          return (
            <div key={label} className="min-w-[100px] flex-1 rounded-lg border border-gray-100 p-2 dark:border-gray-700">
              <div className="text-xs text-gray-500 mb-1">{label}</div>
              <div className="text-lg font-bold" style={{ color }}>
                {v}
              </div>
              <div className="mt-1 h-1.5 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div className="h-full rounded" style={{ width: `${pct}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [chart]);

  if (loading) {
    return (
      <MainLayout>
        <div className="custom-font py-24 text-center" style={{ fontFamily: 'CustomFont' }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  if (!ticket) {
    return (
      <MainLayout>
        <div className="custom-font p-6" style={{ fontFamily: 'CustomFont' }}>
          <Empty description="تیکت یافت نشد" />
          <Button className="mt-4" onClick={goBackToList}>
            بازگشت
          </Button>
        </div>
      </MainLayout>
    );
  }

  const st = STATUS_META[ticket.status] || { text: ticket.status, color: 'default' };
  const pr = PRIORITY_META[ticket.priority] || { text: ticket.priority, color: 'default' };

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-sky-50 dark:bg-sky-900/25">
                <CustomerServiceOutlined className="text-xl text-sky-600 dark:text-sky-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl font-bold text-gray-800 dark:text-gray-100 sm:text-2xl truncate">{ticket.subject}</h1>
                <div className="mt-1 flex flex-wrap gap-2">
                  <Tag color={st.color}>{st.text}</Tag>
                  <Tag color={pr.color}>{pr.text}</Tag>
                  <Text type="secondary">#{ticket.id}</Text>
                </div>
              </div>
            </div>
            <Button icon={<ArrowRightOutlined />} onClick={goBackToList}>
              بازگشت به لیست
            </Button>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50 space-y-4">
            {chartBars ? (
              <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800" title="خلاصه وضعیت‌ها">
                {chartBars}
              </Card>
            ) : null}

            <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800">
              <div className="text-xs text-gray-500 mb-1">توضیحات اولیه</div>
              <ReplyHtml html={ticket.description} />
              <Divider />
              <div className="text-xs text-gray-500 mb-1">درخواست‌دهنده</div>
              <Text>{ticket.requester?.name || '—'}</Text>
              {ticket.requester?.email ? (
                <div className="text-sm mt-1" dir="ltr">
                  {ticket.requester.email}
                </div>
              ) : null}
            </Card>

            <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800" title="ویرایش اطلاعات تیکت">
              <Form form={otherForm} layout="vertical" onFinish={onOtherSave}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                  <Form.Item name="agent_id" label="پشتیبان" rules={[{ required: true }]}>
                    <Select popupClassName="custom-font">
                      {agents.map((a) => (
                        <Option key={a.id} value={a.id}>
                          {a.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="type_id" label="نوع" rules={[{ required: true }]}>
                    <Select popupClassName="custom-font">
                      {types.map((t) => (
                        <Option key={t.id} value={t.id}>
                          {t.type}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="priority" label="اولویت" rules={[{ required: true }]}>
                    <Select popupClassName="custom-font">
                      {Object.entries(PRIORITY_META).map(([k, v]) => (
                        <Option key={k} value={k}>
                          {v.text}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="status" label="وضعیت" rules={[{ required: true }]}>
                    <Select popupClassName="custom-font">
                      {Object.entries(STATUS_META).map(([k, v]) => (
                        <Option key={k} value={k}>
                          {v.text}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <Button type="primary" htmlType="submit" loading={otherSubmitting} className={PANEL_PRIMARY_BTN_CLASS}>
                  ذخیره اطلاعات
                </Button>
              </Form>
            </Card>

            <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800" title="پاسخ / تغییر وضعیت">
              <Form form={replyForm} layout="vertical" onFinish={onReply}>
                <Form.Item name="status" label="وضعیت" rules={[{ required: true }]}>
                  <Select popupClassName="custom-font">
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <Option key={k} value={k}>
                        {v.text}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="message" label="پیام" rules={[{ required: true, message: 'متن پیام را وارد کنید' }]}>
                  <TextArea rows={4} />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={replySubmitting} className={PANEL_PRIMARY_BTN_CLASS}>
                  ارسال
                </Button>
              </Form>
            </Card>

            <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800" title="گفتگو">
              {(ticket.reply || []).length === 0 ? (
                <Text type="secondary">پاسخی ثبت نشده</Text>
              ) : (
                <List
                  dataSource={ticket.reply || []}
                  renderItem={(item) => (
                    <List.Item key={item.id} className="!block border-b border-gray-100 last:border-0 dark:border-gray-700 py-4">
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                        <Text strong>{item.user?.name || 'کاربر'}</Text>
                        <Space>
                          <Text type="secondary" className="text-xs">
                            #{item.id}
                          </Text>
                          <Popconfirm title="حذف این پاسخ؟" onConfirm={() => onDeleteReply(item.id)}>
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              loading={actionId === `r-${item.id}`}
                            >
                              حذف پاسخ
                            </Button>
                          </Popconfirm>
                        </Space>
                      </div>
                      <ReplyHtml html={item.message} />
                      {(item.files || []).length ? (
                        <div className="mt-3 space-y-1">
                          {(item.files || []).map((f) => (
                            <div key={f.id} className="flex flex-wrap items-center gap-2 text-sm">
                              <PaperClipOutlined className="text-gray-400" />
                              <Button type="link" size="small" className="p-0 h-auto" onClick={() => onDownloadFile(f.id)}>
                                {f.filename || `file-${f.id}`}
                              </Button>
                              <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => onDownloadFile(f.id)}>
                                دانلود
                              </Button>
                              <Popconfirm title="حذف فایل؟" onConfirm={() => onDeleteFile(f.id)}>
                                <Button type="link" danger size="small" loading={actionId === `f-${f.id}`}>
                                  حذف فایل
                                </Button>
                              </Popconfirm>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SupportTicketShow;
